import { useMemo } from "react";
import type { DemoAttendeeRow } from "../sql";
import { groupCountryFallback } from "../helpers";
import type { ViewMode } from "../helpers";
import { COUNTRY_GROUPS, fmtN, fmtPct } from "../../../lib/funnel/helpers";
import type { CountryGroup } from "../../../lib/funnel/helpers";
import { PRIMARY } from "../../../lib/funnel/theme";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

// Per-region mini funnel: demos · attendees · signed up · activated · paid.
// Country comes from the matched user country with fallbacks via lead_cal,
// then the email TLD, otherwise "Rest of World".

const FLAGS: Record<CountryGroup, string> = {
  NZ: "🇳🇿",
  AU: "🇦🇺",
  GB: "🇬🇧",
  US: "🇺🇸",
  "Rest of World": "🌐",
};

type Stats = {
  group: CountryGroup;
  demos: number;
  attendees: number;
  signedUp: number;
  activated: number;
  paid: number;
};

export function GeoSplit({ rows, viewMode, pagePills }: { rows: DemoAttendeeRow[]; viewMode: ViewMode; pagePills?: FilterPillSpec[] }) {
  const isCompany = viewMode === "company";
  const data = useMemo<Stats[]>(() => {
    // Pin each demo to a single primary region (first attendee's resolved
    // country, in row order). Without this, multi-region demos got counted in
    // every region's demo Set and the per-region demo total summed past the
    // headline.
    const demoRegion = new Map<string, CountryGroup>();
    for (const r of rows) {
      if (!demoRegion.has(r.demo_external_id)) {
        demoRegion.set(r.demo_external_id, groupCountryFallback(r));
      }
    }

    const map = new Map<CountryGroup, { demos: Set<string>; attendees: number; signedUp: number; activated: number; paid: number }>();
    for (const grp of COUNTRY_GROUPS) {
      map.set(grp, { demos: new Set(), attendees: 0, signedUp: 0, activated: 0, paid: 0 });
    }
    for (const r of rows) {
      const grp = demoRegion.get(r.demo_external_id) ?? groupCountryFallback(r);
      const cur = map.get(grp);
      if (!cur) continue;
      cur.demos.add(r.demo_external_id);
      cur.attendees += 1;
      if (r.signup_timing_bucket !== "never") cur.signedUp += 1;
      if (r.activated) cur.activated += 1;
      if (r.on_paid_plan === true) cur.paid += 1;
    }
    return COUNTRY_GROUPS
      .map((g) => {
        const v = map.get(g);
        if (!v) return null;
        return { group: g, demos: v.demos.size, attendees: v.attendees, signedUp: v.signedUp, activated: v.activated, paid: v.paid };
      })
      .filter((x): x is Stats => x !== null && x.attendees > 0);
  }, [rows]);

  if (data.length === 0) {
    return (
      <div className="chart-tile">
        <h3>Geo split</h3>
        <div className="empty" style={{ padding: 24 }}>No country data available.</div>
      </div>
    );
  }

  return (
    <section className="card">
      <SectionCardHeader
        title="Geo split"
        subtitle={`Per-region mini funnel. Each demo and its ${isCompany ? "company" : "attendees"} are pinned to one region (first attendee's country: matched user, then booking, then email TLD).`}
        filterPills={pagePills}
      />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(data.length, 5)}, 1fr)`, gap: 12 }}>
        {data.map((d) => (
          <GeoCard key={d.group} stats={d} isCompany={isCompany} />
        ))}
      </div>
    </section>
  );
}

function GeoCard({ stats, isCompany }: { stats: Stats; isCompany: boolean }) {
  const unitLabel = isCompany ? "Companies" : "Attendees";
  const stages = [
    { label: "Demos", count: stats.demos, denom: stats.demos },
    { label: unitLabel, count: stats.attendees, denom: stats.attendees },
    { label: "Signed up", count: stats.signedUp, denom: stats.attendees },
    { label: "Activated", count: stats.activated, denom: stats.attendees },
    { label: "Paid", count: stats.paid, denom: stats.attendees },
  ];
  return (
    <div style={{
      border: "1px solid var(--border-default)", borderRadius: 8, padding: 12,
      background: "var(--bg-elevated, #fff)",
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        {FLAGS[stats.group]} {stats.group}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {stages.map((s, i) => {
          const pct = i < 2 ? null : (s.count / Math.max(1, s.denom)) * 100;
          const widthPct = i < 2 ? 100 : pct ?? 0;
          return (
            <div key={s.label}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--fg-2)" }}>
                <span>{s.label}</span>
                <span>
                  {fmtN(s.count)}
                  {pct != null && <span style={{ color: "var(--fg-3)", marginLeft: 6 }}>{fmtPct(s.count, s.denom)}</span>}
                </span>
              </div>
              <div style={{ height: 4, background: "rgba(0,0,0,0.05)", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${widthPct}%`, background: PRIMARY }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
